import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Rocket, PartyPopper } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ToDoList, ToDoCreateForm, EditTodoModal } from '../components/todos';
import {
  getTodosWithSource,
  createTodo,
  updateTodo,
  toggleTodoCompleted,
  deleteTodo,
  categorizeTodo,
} from '../services/todoService';
import { useAgentRefresh, QUERY_KEYS } from '../ai/hooks/useAgentRefresh';
import { AGENT_FOCUS_EVENT } from '../ai/context/AIAgentContext';
import type { UIFocus } from '../ai/types/agent';
import type { TodoWithSource, CreateTodoInput, UpdateTodoInput } from '../types/todo';
import { logger } from '../lib/logger';

function getMotivationMessage(completed: number, total: number): { text: string; icon: 'rocket' | 'party' } {
  if (total === 0) return { text: 'Keine Aufgaben für heute', icon: 'rocket' };
  if (completed === 0) return { text: 'Starte jetzt!', icon: 'rocket' };

  const percentage = (completed / total) * 100;

  if (percentage === 100) return { text: 'Super! Für heute alles erledigt!', icon: 'party' };
  if (percentage >= 80) return { text: 'Fast geschafft!', icon: 'rocket' };
  if (percentage >= 50) return { text: 'Die Hälfte ist geschafft!', icon: 'rocket' };
  if (percentage >= 25) return { text: 'Weiter so!', icon: 'rocket' };
  return { text: 'Gib Gas!', icon: 'rocket' };
}

export function Todos() {
  const { user } = useAuth();
  const [todos, setTodos] = useState<TodoWithSource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTodo, setEditingTodo] = useState<TodoWithSource | null>(null);
  const [focusedTodoId, setFocusedTodoId] = useState<string | null>(null);
  const todoRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const loadTodos = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const data = await getTodosWithSource(user.id);
      setTodos(data);
    } catch (error) {
      logger.error('Error loading todos:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadTodos();
    }
  }, [user?.id, loadTodos]);

  // Subscribe to agent actions for automatic refresh
  useAgentRefresh([QUERY_KEYS.TODOS], loadTodos);

  // Listen for AI Agent focus events
  useEffect(() => {
    const handleFocus = (event: Event) => {
      const customEvent = event as CustomEvent<UIFocus>;
      const focus = customEvent.detail;

      if (focus.type === 'todo') {
        setFocusedTodoId(focus.id);

        // Scroll to the focused todo after a short delay
        setTimeout(() => {
          const todoElement = todoRefs.current.get(focus.id);
          if (todoElement) {
            todoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 150);

        // Clear focus highlight after 3 seconds
        setTimeout(() => {
          setFocusedTodoId(null);
        }, 3000);
      }
    };

    window.addEventListener(AGENT_FOCUS_EVENT, handleFocus);
    return () => window.removeEventListener(AGENT_FOCUS_EVENT, handleFocus);
  }, []);

  const handleCreate = async (input: CreateTodoInput) => {
    if (!user?.id) return;
    await createTodo(user.id, input);
    await loadTodos();
    setShowCreateForm(false);
  };

  const handleToggle = async (id: string, completed: boolean) => {
    if (!user?.id) return;
    try {
      await toggleTodoCompleted(id, user.id, completed);
      // Optimistically update the todo's completed status while keeping source data
      setTodos((prev) =>
        prev.map((t) => (t.id === id ? { ...t, completed } : t))
      );
    } catch (error) {
      logger.error('Error toggling todo:', error);
      // Reload to restore correct state on error
      await loadTodos();
    }
  };

  const handleDelete = async (id: string) => {
    if (!user?.id) return;
    try {
      await deleteTodo(id, user.id);
      setTodos((prev) => prev.filter((t) => t.id !== id));
    } catch (error) {
      logger.error('Error deleting todo:', error);
    }
  };

  const handleEdit = (todo: TodoWithSource) => {
    setEditingTodo(todo);
  };

  const handleSaveEdit = async (id: string, input: UpdateTodoInput) => {
    if (!user?.id) return;
    try {
      await updateTodo(id, user.id, input);
      await loadTodos();
    } catch (error) {
      logger.error('Error updating todo:', error);
      throw error;
    }
  };

  // Filter for today's todos only (for progress)
  const todaysTodos = todos.filter((t) => {
    const group = categorizeTodo(t.due_date, false); // Check raw due date
    return group === 'TODAY';
  });
  const todaysCompleted = todaysTodos.filter((t) => t.completed).length;
  const todaysTotal = todaysTodos.length;
  const todaysPercentage = todaysTotal > 0 ? Math.round((todaysCompleted / todaysTotal) * 100) : 0;
  const motivation = getMotivationMessage(todaysCompleted, todaysTotal);

  // Total counts for subtitle
  const completedCount = todos.filter((t) => t.completed).length;
  const totalCount = todos.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] dark:text-white mb-1">
            To-Dos
          </h1>
          <p className="text-[#6B6B6B] dark:text-slate-400">
            {totalCount > 0
              ? `${completedCount} von ${totalCount} Aufgaben erledigt`
              : 'Verwalte deine täglichen Aufgaben'}
          </p>
        </div>
        {!showCreateForm && (
          <button
            data-tour="create-todo"
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            <span>Neue Aufgabe</span>
          </button>
        )}
      </div>

      {/* Progress für heute */}
      <div data-tour="todo-progress" className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 shadow-sm">
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            motivation.icon === 'party'
              ? 'bg-emerald-100 dark:bg-emerald-900/30'
              : 'bg-amber-100 dark:bg-amber-900/30'
          }`}>
            {motivation.icon === 'party' ? (
              <PartyPopper className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <Rocket className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-semibold ${
                motivation.icon === 'party'
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : 'text-amber-700 dark:text-amber-400'
              }`}>
                {motivation.text}
              </span>
              <span className={`text-sm font-bold ${
                motivation.icon === 'party'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-amber-600 dark:text-amber-400'
              }`}>
                {todaysCompleted}/{todaysTotal} heute
              </span>
            </div>
            <div className="h-2.5 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  motivation.icon === 'party'
                    ? 'bg-gradient-to-r from-emerald-400 to-emerald-500'
                    : 'bg-gradient-to-r from-amber-400 to-orange-400'
                }`}
                style={{ width: `${todaysPercentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {showCreateForm && (
        <ToDoCreateForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      <div data-tour="todo-groups">
        <ToDoList
          todos={todos}
          onToggle={handleToggle}
          onDelete={handleDelete}
          onEdit={handleEdit}
          focusedTodoId={focusedTodoId}
          todoRefs={todoRefs}
        />
      </div>

      <EditTodoModal
        isOpen={!!editingTodo}
        todo={editingTodo}
        onClose={() => setEditingTodo(null)}
        onSave={handleSaveEdit}
      />
    </div>
  );
}
